import { Injectable } from '@angular/core';
import createDebug from 'debug';

const debug = createDebug('PhotoCaliper:PageService');

export interface PageMetadata {
  name: string;
  path: string;
  displayName: string;
  description: string;
  sidenavIcon?: string;
  isDefault: boolean;
  roles: readonly string[];
}

@Injectable({ providedIn: 'root' })
export class PageService {
  readonly names = [
    'Login',
    'ManageUsers',
    'MeasurementSet',
    'MeasurementType',
    'MeasurementTypes',
    'NotAuthorized',
    'PersonalAccessTokens',
    'Sale',
    'SalePhoto',
    'Sales',
    'SwitchUser',
  ] as const;

  readonly metadata: readonly PageMetadata[] = [
    {
      name: 'Login',
      path: '/login',
      displayName: 'Login',
      description: 'Public login page with a button to initiate OIDC login.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'ManageUsers',
      path: '/manage-users',
      displayName: 'Manage Users',
      description:
        'Admin page listing users and their role assignments. The manage-role-assignments component renders a row per user and a checkbox per role to grant or revoke it.',
      sidenavIcon: 'manage_accounts',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'MeasurementSet',
      path: '/measurement-sets/:measurementSet.id',
      displayName: 'Measurement Set',
      description:
        "Detail page: the set's measurements as an accordion on the left, the sale photo on the right. Expand a measurement, then click the photo to drop its endpoint coordinates into that measurement.",
      isDefault: false,
      roles: [],
    },
    {
      name: 'MeasurementType',
      path: '/measurement-types/:measurementType.id',
      displayName: 'Measurement Type',
      description: 'Detail page for a single measurement type.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'MeasurementTypes',
      path: '/measurement-types',
      displayName: 'Measurement Types',
      description:
        "Admin page: the editable catalog of metrics the tool can capture, shown as a draggable accordion. Each panel edits a type's fields; drag the handle to reorder (sequence), use the button to designate the scaling reference, and use the add field below to create new ones.",
      sidenavIcon: 'straighten',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'NotAuthorized',
      path: '/not-authorized',
      displayName: 'Not Authorized',
      description:
        'Displayed when a user authenticates but is not registered in the system.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'PersonalAccessTokens',
      path: '/personal-access-tokens',
      displayName: 'Personal Access Tokens',
      description:
        'Page where the current user manages their personal access tokens. Reached from the avatar menu; not shown in the sidenav.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Sale',
      path: '/sales/:sale.id',
      displayName: 'Sale',
      description:
        "Detail page: sale fields on the left, the sale's photos on the right.",
      isDefault: false,
      roles: [],
    },
    {
      name: 'SalePhoto',
      path: '/sale-photos/:salePhoto.id',
      displayName: 'Sale Photo',
      description:
        'Detail page for a single sale photo: catalog metadata on the left, the image on the right.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Sales',
      path: '/sales',
      displayName: 'Sales',
      description:
        'Landing page listing the sales whose catalog photos are measured.',
      sidenavIcon: 'gavel',
      isDefault: true,
      roles: [],
    },
    {
      name: 'SwitchUser',
      path: '/switch-user',
      displayName: 'Switch User',
      description:
        'Allows administrators to switch to another user by selecting from a list. Only available when ALLOW_IMPERSONATION is enabled on the server. Selecting a user sets sessionStorage and reloads the app.',
      isDefault: false,
      roles: [],
    },
  ];

  readonly sidenavPages: readonly PageMetadata[] = [
    this.metadata.find((p) => p.name === 'Sales')!,
    this.metadata.find((p) => p.name === 'MeasurementTypes')!,
    this.metadata.find((p) => p.name === 'ManageUsers')!,
  ];

  getMetadata(name: string): PageMetadata | undefined {
    return this.metadata.find((m) => m.name === name);
  }
}
